import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Dropdown } from 'react-bootstrap';
import { FaTasks } from 'react-icons/fa';
import apiFetch from '../../../utils/apiFetch';
import { notify } from '../../../utils/notification';
import { SPELL_REFRESH_EVENT } from '../utils/spellRefreshEvents';

/** The DM's to-do list: what spells leave to the table. The button shows how many are open; play never waits on them. */
export default function DmNotes({ campaignId }) {
  const [notes, setNotes] = useState([]);
  const latest = useRef(0);
  const base = `/campaigns/${encodeURIComponent(campaignId)}/dm-notes`;
  const load = useCallback(async () => {
    const request = ++latest.current;
    const response = await apiFetch(base, { cache: 'no-store' });
    if (!response.ok) return;
    const result = await response.json();
    if (request === latest.current) setNotes(result.notes || []);
  }, [base]);
  useEffect(() => {
    if (!campaignId) return undefined;
    load().catch(() => { /* The next update retries. */ });
    const refresh = (event) => { if (event.detail?.campaignId === campaignId && event.detail?.dmNotes) load().catch(() => {}); };
    window.addEventListener(SPELL_REFRESH_EVENT, refresh);
    return () => window.removeEventListener(SPELL_REFRESH_EVENT, refresh);
  }, [campaignId, load]);
  const clear = async (path) => {
    latest.current += 1;
    try {
      const response = await apiFetch(`${base}${path}/clear`, { method: 'POST' });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'The note could not be cleared.');
      setNotes(result.notes || []);
    } catch (error) { notify(error.message, 'danger'); }
  };
  const decide = async (note, approve) => {
    latest.current += 1;
    try {
      const response = await apiFetch(`${base}/${encodeURIComponent(note.id)}/decide`, { method: 'POST', body: JSON.stringify({ approve }) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'The request could not be answered.');
      setNotes(result.notes || []);
    } catch (error) { notify(error.message, 'danger'); }
  };
  return (
    <Dropdown drop="up" autoClose="outside">
      <Dropdown.Toggle id="dm-notes-menu" variant="outline-light" className="zombies-dm-bottom-bar__button" aria-label={`DM notes: ${notes.length} open`}>
        <FaTasks className="zombies-dm-bottom-bar__icon" aria-hidden="true" />
        <span>DM notes</span>
        {notes.length > 0 && <Badge bg="warning" text="dark" className="ms-1">{notes.length}</Badge>}
      </Dropdown.Toggle>
      <Dropdown.Menu variant="dark" style={{ width: 340, maxHeight: '60vh', overflowY: 'auto', padding: 8 }}>
        {!notes.length && <p className="text-muted small mb-0 px-1">Nothing to do right now. Spells add a note here when something needs a hand.</p>}
        {notes.map((note) => note.approval ? <div key={note.id} className="small px-1 py-2 border-bottom border-secondary">
          <strong className="d-block">{note.spell} · {note.target}</strong>{note.text}
          <div className="d-flex gap-2 mt-2">
            <Button size="sm" variant="success" onClick={() => decide(note, true)}>Approve</Button>
            <Button size="sm" variant="outline-light" onClick={() => decide(note, false)}>Deny</Button>
          </div>
        </div> : <label key={note.id} className="d-flex gap-2 align-items-start small px-1 py-2 border-bottom border-secondary">
          <input type="checkbox" className="mt-1" aria-label={`Clear ${note.spell}${note.target ? `: ${note.target}` : ''}`} onChange={() => clear(`/${encodeURIComponent(note.id)}`)} />
          <span><strong className="d-block">{note.spell}{note.target ? ` · ${note.target}` : ''}</strong>{note.text}</span>
        </label>)}
        {notes.filter((note) => !note.approval).length > 1 && <Button size="sm" variant="outline-light" className="mt-2 w-100" onClick={() => clear('')}>Clear all</Button>}
      </Dropdown.Menu>
    </Dropdown>
  );
}
